import React, { useState } from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { useCollaborations } from '../context/CollaborationsContext';
import { formatEuro } from '../utils/helpers';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from './ui/table';
import { cn } from '../lib/utils';

const MONTH_NAMES = [
  'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
  'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic',
];

const SORT_OPTIONS = [
  { key: 'income', label: 'Ingresos' },
  { key: 'cost',   label: 'Coste' },
  { key: 'profit', label: 'Beneficio' },
  { key: 'roas',   label: 'ROAS' },
];

export default function VentasPage() {
  const { collaborations } = useCollaborations();
  const [sortBy, setSortBy] = useState('income');
  const [asc, setAsc] = useState(false);

  const rows = collaborations.map((c) => {
    const income = Number(c.income) || 0;
    const cost = Number(c.cost) || 0;
    const profit = income - cost;
    const roas = cost > 0 ? income / cost : null;
    return { ...c, income, cost, profit, roas };
  });

  rows.sort((a, b) => {
    const av = a[sortBy] ?? -Infinity;
    const bv = b[sortBy] ?? -Infinity;
    return asc ? av - bv : bv - av;
  });

  const totalIncome = rows.reduce((s, r) => s + r.income, 0);
  const totalCost = rows.reduce((s, r) => s + r.cost, 0);
  const totalProfit = totalIncome - totalCost;
  const totalRoas = totalCost > 0 ? (totalIncome / totalCost).toFixed(2) : '—';

  const monthMap = {};
  collaborations.forEach((c) => {
    const date = new Date(c.createdAt || c.updatedAt);
    if (isNaN(date)) return;
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    if (!monthMap[key])
      monthMap[key] = {
        mes: MONTH_NAMES[date.getMonth()] + ' ' + date.getFullYear(),
        ingresos: 0,
        coste: 0,
      };
    monthMap[key].ingresos += Number(c.income) || 0;
    monthMap[key].coste += Number(c.cost) || 0;
  });
  const chartData = Object.keys(monthMap)
    .sort()
    .map((k) => ({
      ...monthMap[k],
      beneficio: +(monthMap[k].ingresos - monthMap[k].coste).toFixed(2),
      ingresos: +monthMap[k].ingresos.toFixed(2),
      coste: +monthMap[k].coste.toFixed(2),
    }));

  function toggleSort(key) {
    if (sortBy === key) setAsc((a) => !a);
    else {
      setSortBy(key);
      setAsc(false);
    }
  }

  const totals = [
    { label: 'Ingresos totales', value: formatEuro(totalIncome), color: 'text-green-600' },
    { label: 'Coste total', value: formatEuro(totalCost), color: 'text-destructive' },
    {
      label: 'Beneficio total',
      value: formatEuro(totalProfit),
      color: totalProfit >= 0 ? 'text-green-600' : 'text-destructive',
    },
    { label: 'ROAS global', value: totalRoas, color: 'text-primary' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Ventas</h1>
          <p className="text-muted-foreground text-sm">Estadísticas por colaboración</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {totals.map((card) => (
          <Card key={card.label}>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs uppercase text-muted-foreground font-medium tracking-wider">
                {card.label}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={cn('text-2xl font-bold', card.color)}>{card.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Evolución mensual</CardTitle>
        </CardHeader>
        <CardContent>
          {chartData.length < 2 ? (
            <div className="text-center text-sm text-muted-foreground py-10">
              Se necesitan al menos 2 meses de datos para mostrar el gráfico
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradIngresos" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#16a34a" stopOpacity={0.18} />
                    <stop offset="95%" stopColor="#16a34a" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradCoste" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#d2003b" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#d2003b" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradBeneficio" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0f172a" stopOpacity={0.12} />
                    <stop offset="95%" stopColor="#0f172a" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis
                  dataKey="mes"
                  tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => `${v.toLocaleString('es-ES')} €`}
                  width={80}
                />
                <Tooltip
                  contentStyle={{
                    background: 'var(--card)',
                    border: '1px solid var(--border)',
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  formatter={(value, name) => [
                    formatEuro(value),
                    name.charAt(0).toUpperCase() + name.slice(1),
                  ]}
                  labelStyle={{ fontWeight: 700, marginBottom: 4, color: 'var(--foreground)' }}
                />
                <Legend
                  wrapperStyle={{ fontSize: 12, paddingTop: 12 }}
                  formatter={(v) => v.charAt(0).toUpperCase() + v.slice(1)}
                />
                <Area
                  type="monotone"
                  dataKey="ingresos"
                  stroke="#16a34a"
                  strokeWidth={2}
                  fill="url(#gradIngresos)"
                  dot={false}
                  activeDot={{ r: 4 }}
                />
                <Area
                  type="monotone"
                  dataKey="coste"
                  stroke="#d2003b"
                  strokeWidth={2}
                  fill="url(#gradCoste)"
                  dot={false}
                  activeDot={{ r: 4 }}
                />
                <Area
                  type="monotone"
                  dataKey="beneficio"
                  stroke="#0f172a"
                  strokeWidth={2}
                  fill="url(#gradBeneficio)"
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="pl-6">Influencer</TableHead>
                {SORT_OPTIONS.map((o) => (
                  <TableHead
                    key={o.key}
                    onClick={() => toggleSort(o.key)}
                    className="text-right cursor-pointer select-none whitespace-nowrap"
                  >
                    {o.label} {sortBy === o.key ? (asc ? '↑' : '↓') : ''}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="pl-6">
                    <div className="text-sm font-semibold whitespace-nowrap">{row.name}</div>
                    {row.usuario && (
                      <div className="text-xs text-muted-foreground">{row.usuario}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-semibold">
                    {formatEuro(row.income)}
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground">
                    {formatEuro(row.cost)}
                  </TableCell>
                  <TableCell
                    className={cn(
                      'text-right font-bold',
                      row.profit >= 0 ? 'text-green-600' : 'text-destructive',
                    )}
                  >
                    {formatEuro(row.profit)}
                  </TableCell>
                  <TableCell
                    className={cn(
                      'text-right font-bold',
                      row.roas === null
                        ? 'text-muted-foreground'
                        : row.roas >= 1
                        ? 'text-green-600'
                        : 'text-destructive',
                    )}
                  >
                    {row.roas === null ? '—' : Number(row.roas).toFixed(2)}
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="py-10 text-center text-sm text-muted-foreground"
                  >
                    No hay colaboraciones
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
